import { type FormEvent } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import Logo from '../../components/base/Logo';
import { ShieldCheck, Eye, EyeOff } from 'lucide-react';

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => { setLoading(false); navigate('/'); }, 1200);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="flex items-center justify-center min-h-screen px-4 py-24">
        <div className="w-full max-w-[420px]">
          <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-8">
            <div className="flex justify-center mb-8">
              <Logo variant="dark" size="lg" />
            </div>
            <h1 className="font-display text-2xl font-bold text-slate-900 text-center mb-1">{t('login.title')}</h1>
            <p className="text-slate-500 text-sm text-center mb-8">{t('login.subtitle')}</p>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('login.email')}</label>
                <input type="email" placeholder={t('login.email_placeholder')} required
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-medium text-slate-700">{t('login.password')}</label>
                  <button type="button" className="text-xs text-primary-600 hover:text-primary-700 font-medium">{t('login.forgot')}</button>
                </div>
                <div className="relative">
                  <input type={showPw ? 'text' : 'password'} placeholder={t('login.password_placeholder')} required
                    className="w-full px-4 py-3 pr-11 rounded-xl border border-slate-200 text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 rounded border-slate-300 accent-primary-600" />
                <span className="text-sm text-slate-600">{t('login.remember')}</span>
              </label>
              <button type="submit" disabled={loading}
                className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3.5 rounded-xl transition-all duration-200 disabled:opacity-60 text-sm">
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
                    Yüklənir...
                  </span>
                ) : t('login.btn')}
              </button>
            </form>
            <p className="text-center text-sm text-slate-500 mt-6">
              {t('login.no_account')}{' '}
              <Link to="/register" className="text-primary-600 hover:text-primary-700 font-semibold">{t('login.register_link')}</Link>
            </p>
            <div className="flex items-center justify-center gap-1.5 mt-6 text-slate-400">
              <ShieldCheck size={14} className="text-emerald-500" />
              <span className="text-xs">{t('login.secure')}</span>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
